import type { FieldErrorsImpl, FieldValues, UseFormRegister } from 'react-hook-form'

import { useFormContext } from 'react-hook-form'
import React from 'react'
import { cn } from '@/utils/ui'

import { Error } from '../Error'
import { Width } from '../Width'
import { PhoneFieldFormBlock } from '@/payload-types'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { FormLabel } from '@/ui/atoms/FormLabel'
import { useTranslations } from 'next-intl'

export const Phone: React.FC<
  PhoneFieldFormBlock & {
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any
      }>
    >
    getValues: any
    register: UseFormRegister<FieldValues>
    setValue: any
  }
> = ({ name, errors, description, defaultValue, width, icon, label, register, required }) => {
  const { setValue, watch } = useFormContext()
  const value = watch(name)
  const [hasInteracted, setHasInteracted] = React.useState(false)
  const [isValid, setIsValid] = React.useState(false)
  const t = useTranslations('forms')

  React.useEffect(() => {
    if (defaultValue && !hasInteracted) {
      setValue(name, defaultValue)
      setIsValid(true)
    }
  }, [defaultValue, hasInteracted, name, setValue])

  const isValidPhone = (value: string) => {
    if (!value || value.length < 6) {
      setIsValid(false)
      return false
    }
    const phoneRegex =
      /(?:([+]\d{1,4})[-.\s]?)?(?:[(](\d{1,3})[)][-.\s]?)?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})/
    const valid = phoneRegex.test(value)
    setIsValid(valid)
    return valid
  }

  const handleChange = (value: string) => {
    setHasInteracted(true)
    if (isValidPhone(value)) {
      setValue(name, value)
    } else {
      setValue(name, '')
    }
  }

  return (
    <Width width={width ?? 100}>
      <div>
        <div className="grid gap-1 mb-2">
          <FormLabel htmlFor={name} description={description}>
            {label}
          </FormLabel>
        </div>
        <PhoneInput
          value={value || ''}
          inputClassName={cn(
            '!text-body-XS-regular w-full bg-background px-3 py-2 text-text-faded',
            value && 'text-text-base',
            'focus-visible:outline-none focus:ring-0',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'placeholder:text-text-faded',
            'rounded-r-md',
          )}
          className={cn(
            'flex min-h-[35px] rounded-md mt-2',
            errors[name]
              ? 'border border-border-error bg-bg-error'
              : 'border border-border-input hover:border-border-input-hover bg-background',
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-border-focus focus-within:ring-offset-2',
          )}
          defaultCountry="us"
          forceDialCode={true}
          onChange={handleChange}
          onFocus={() => setHasInteracted(true)}
          placeholder={!value && !hasInteracted ? (defaultValue ?? undefined) : undefined}
        />
        {errors[name] && <Error message={t('invalid-phone')} />}
      </div>
    </Width>
  )
}
